import * as actionTypes from '../constants/actionTypes';
import { getExternalPrototypeUrl } from '../selectors';
import { getTimestamp } from './events';
import * as figmaEvents from './figmaEvents';
var EVENTS = [];
var callbacks;
var initialScreen = null;
var hotspot = null;
var pendingClickEvent = null;
var hitGoalScreen = false;
function handleEvent(event) {
    if (callbacks.handleEvent) {
        EVENTS.push(event);
        callbacks.handleEvent(event, EVENTS);
    }
}
// Note it's important we don't destructure the origCallbacks object here
// as handleEvent can change during the lifetime of the prototype viewer
// if the user leaves the tab that a ballpark test is running in and comes
// back to it.
export default (function (origCallbacks) {
    // Reset the global state when creating a new middleware
    // (really we need a better solution here to handle multiple instances of the viewer
    // or a new project being loaded into the same instance -- when we added user testing
    // we did it quickly as we could assume we wouldn't have multiple instances of the viewer
    // or setup/teardown instances or load a new project into an existing instance. Ballpark
    // is breaking these assumptions. Safest is to put this state into the store so it's
    // not global and can be reset on each new project load.)
    callbacks = origCallbacks;
    EVENTS = [];
    initialScreen = null;
    hotspot = null;
    pendingClickEvent = null;
    hitGoalScreen = false;
    return function (action, state) {
        if (!getExternalPrototypeUrl(state))
            return;
        switch (action.type) {
            case actionTypes.REPLAY_START_RECORDING:
                pendingClickEvent = null;
                hotspot = null;
                hitGoalScreen = false;
                handleEvent(figmaEvents.startRecording());
                handleEvent(figmaEvents.initialScreen({ id: initialScreen.id }));
                break;
            case actionTypes.REPLAY_STOP_RECORDING:
                if (pendingClickEvent) {
                    handleEvent(figmaEvents.missedClick(pendingClickEvent));
                }
                handleEvent(figmaEvents.stopRecording());
                break;
            case actionTypes.EXTERNAL_PROTOTYPE_LOADING:
                callbacks.onPlayerLoading();
                break;
            default:
                break;
        }
    };
});
export function handleFigmaEvents(e, setIsLoading, screens, goalScreenPk) {
    var _a, _b, _c, _d, _e, _f;
    var _g = e.data, data = _g.data, type = _g.type;
    if (type) {
        if (type === 'INITIAL_LOAD') {
            setIsLoading('loaded');
            callbacks.onPlayerReady();
        }
        else if (type === 'MOUSE_PRESS_OR_RELEASE') {
            if (pendingClickEvent) {
                handleEvent(figmaEvents.missedClick(pendingClickEvent));
                pendingClickEvent = null;
            }
            // If you click outside of the prototype in the iframe
            // targetNodeId will be null. We don't want to
            // send an event in that case
            if (data.targetNodeId) {
                var targetNodeId = data.targetNodeId, presentedNodeId = data.presentedNodeId, nearestScrollingFrameMousePosition = data.nearestScrollingFrameMousePosition, nearestScrollingFrameOffset = data.nearestScrollingFrameOffset;
                // sometimes figma doesn't send the nearestScrollingFrameMousePosition which crashes the app
                // instead we will return early and not send an event (as its coords are not valid)
                if (!nearestScrollingFrameMousePosition)
                    return;
                var x = nearestScrollingFrameMousePosition.x, y = nearestScrollingFrameMousePosition.y;
                if (nearestScrollingFrameOffset) {
                    x += (nearestScrollingFrameOffset === null || nearestScrollingFrameOffset === void 0 ? void 0 : nearestScrollingFrameOffset.x) || 0;
                    y += (nearestScrollingFrameOffset === null || nearestScrollingFrameOffset === void 0 ? void 0 : nearestScrollingFrameOffset.y) || 0;
                }
                var mouseCoords = [x && Math.round(x), y && Math.round(y)];
                var marvelScreen = getMarvelScreen(presentedNodeId, screens);
                // PRESENTED_NODE_CHANGED doesn't include information on
                // what hotspot brought you to the new screen.
                // Hence why we need to keep track of it here
                setMarvelHotspot(targetNodeId, marvelScreen, mouseCoords);
                // TODO: figure out if the coord data already give us scaled coords
                // If user has hit goal screen already,
                // we don't want to record any more clicks
                if (marvelScreen && !hitGoalScreen) {
                    pendingClickEvent = {
                        id: marvelScreen.id,
                        height: (_a = marvelScreen.img) === null || _a === void 0 ? void 0 : _a.height,
                        width: (_b = marvelScreen.img) === null || _b === void 0 ? void 0 : _b.width,
                        coords: mouseCoords,
                        timestamp: getTimestamp(),
                    };
                }
            }
        }
        else if (type === 'NEW_STATE') {
            var newScreen = getMarvelScreen(data.newVariantId, screens);
            // pendingClickEvents are set in the MousePressOrRelease case
            // this early return will protect us against adding events for hover events on interactive components
            if (!pendingClickEvent)
                return;
            handleEvent(figmaEvents.interactiveChangeEvent({
                newScreenId: newScreen.id,
                hotspot: hotspot,
            }));
            pendingClickEvent = null;
            hotspot = null;
            if (newScreen.id === goalScreenPk) {
                hitGoalScreen = true;
                handleEvent(figmaEvents.goalScreen({
                    id: newScreen.id,
                    width: (_c = newScreen.img) === null || _c === void 0 ? void 0 : _c.width,
                    height: (_d = newScreen.img) === null || _d === void 0 ? void 0 : _d.height,
                }));
            }
        }
        else if (type === 'PRESENTED_NODE_CHANGED') {
            if (!initialScreen) {
                initialScreen = getMarvelScreen(data.presentedNodeId, screens);
                // We have to delay the stopRecording event slightly to make sure that
                // the recording doesn't cut out as soon as someone reaches the goal screen.
                // This way we can still show the last screen in the video playback
                // However, because 750ms is quite an arbitrary number, we want to make sure
                // that no further events get called once the goal screen is reached
            }
            else if (!hitGoalScreen) {
                // MOUSE_PRESS_OR_RELEASE and PRESENTED_NODE_CHANGED events
                // happen one after another.
                // In that case we reset pendingClickEvent to null
                // and only fire the screen transition event
                if (pendingClickEvent)
                    pendingClickEvent = null;
                var newScreen = getMarvelScreen(data.presentedNodeId, screens);
                handleEvent(figmaEvents.screenTransition({
                    newScreenId: newScreen.id,
                    hotspot: hotspot,
                }));
                // When participant uses arrows to navigate a Figma prototype
                // there is no hotspot associated with it.
                // We need to make sure that we clear any hotspots as soon as
                // they are used in a screen transition event.
                hotspot = null;
                if (newScreen.id === goalScreenPk) {
                    hitGoalScreen = true;
                    handleEvent(figmaEvents.goalScreen({
                        id: newScreen.id,
                        width: (_e = newScreen.img) === null || _e === void 0 ? void 0 : _e.width,
                        height: (_f = newScreen.img) === null || _f === void 0 ? void 0 : _f.height,
                    }));
                }
            }
        }
    }
}
function getMarvelScreen(figmaNodeId, marvelScreens) {
    return marvelScreens.find(function (screen) { return screen.externalId === figmaNodeId; });
}
function setMarvelHotspot(figmaNodeId, marvelScreen, mouseCoords) {
    var marvelHotspot = marvelScreen.allHotspots.find(function (currentHotspot) { return currentHotspot.externalId === figmaNodeId; });
    if (marvelHotspot) {
        hotspot = { id: marvelHotspot.id, mouseCoords: mouseCoords };
    }
    else {
        hotspot = null;
    }
}
